import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from './PageHeader';
import { apiClient } from '../services/api';
import type { FaqItem } from '../types';
import './FAQPage.css';

/** Parses text with markdown links [text](url) into React nodes */
function parseAnswerWithLinks(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <Link key={match.index} to={match[2]} className="faq-link">
        {match[1]}
      </Link>
    );
    lastIndex = match.index + match[0].length;
  }
  parts.push(text.slice(lastIndex));

  return parts;
}

interface FAQItemProps {
  item: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}

const FAQItemComponent: React.FC<FAQItemProps> = ({ item, isOpen, onToggle }) => {
  const handleClick = () => {
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
    }
    onToggle();
  };

  const answerNode = parseAnswerWithLinks(item.answer);

  return (
    <div className={`faq-item ${isOpen ? 'open' : ''}`}>
      <button className="faq-question" onClick={handleClick}>
        <span className="faq-question-text">{item.question}</span>
        <svg
          className="faq-chevron"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <div className="faq-answer-wrapper">
        <div className="faq-answer">{answerNode}</div>
      </div>
    </div>
  );
};

export const FAQPage: React.FC = () => {
  const [items, setItems] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    apiClient
      .getFaq()
      .then((data) => {
        if (!cancelled) {
          setItems(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Не удалось загрузить FAQ');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleItem = (id: number) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const grouped = new Map<string | null, FaqItem[]>();
  const sectionOrder: (string | null)[] = [];
  for (const item of items) {
    const key = item.section_title?.trim() || null;
    if (!grouped.has(key)) {
      sectionOrder.push(key);
      grouped.set(key, []);
    }
    grouped.get(key)!.push(item);
  }

  const sections = sectionOrder.map((key) => [key, grouped.get(key)!] as const);

  return (
    <div className="page-container faq-page">
      <PageHeader title="FAQ" />

      {loading && <div className="faq-loading">Загрузка...</div>}
      {error && <div className="faq-error">{error}</div>}

      {!loading && !error && (
        <>
          {sections.map(([sectionTitle, sectionItems]) => (
            <div className="faq-section" key={sectionTitle ?? '__no_section__'}>
              {sectionTitle && (
                <h2 className="faq-section-title">{sectionTitle}</h2>
              )}
              <div className="faq-list">
                {sectionItems.map((item) => (
                  <FAQItemComponent
                    key={item.id}
                    item={item}
                    isOpen={openItems.has(item.id)}
                    onToggle={() => toggleItem(item.id)}
                  />
                ))}
              </div>
            </div>
          ))}

          <div className="faq-footer">
            Не смогли найти ответ на свой вопрос?
            <br />
            <Link to="/support" className="faq-footer-link">
              Напишите нам
            </Link>
          </div>
        </>
      )}
    </div>
  );
};
